import type { Ref } from 'vue'
import { apiErrorMessage } from '@/core/utils/api-error'
import { useToastStore } from '@/shared/stores/toast.store'

const inFlightKeys = new Set<string>()

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

export type BackgroundBlobFetchResult = Blob | { blob: Blob; filename?: string }

export type BackgroundBlobExportOptions = {
  /** Dedupes concurrent starts (defaults to a shared key). */
  key?: string
  /** Optional button/busy flag — page stays usable; only this ref flips. */
  busy?: Ref<boolean>
  /** Extra busy hook for non-boolean UI state (e.g. group key). */
  onBusyChange?: (busy: boolean) => void
  filename: string | (() => string)
  fetchBlob: () => Promise<BackgroundBlobFetchResult>
  startedMessage: string
  successMessage: string | (() => string)
  failedMessage: string
  mapError?: (error: unknown) => string
  onSuccess?: () => void | Promise<void>
}

function resolveMessage(message: string | (() => string)): string {
  return typeof message === 'function' ? message() : message
}

/**
 * Fire-and-forget Excel/blob export: toast immediately, keep UI interactive,
 * download when the response finishes (no full-page lock).
 */
export function runBackgroundBlobExport(options: BackgroundBlobExportOptions): void {
  const key = options.key?.trim() || 'blob-export'
  if (options.busy?.value || inFlightKeys.has(key)) return

  inFlightKeys.add(key)
  if (options.busy) options.busy.value = true
  options.onBusyChange?.(true)

  const toast = useToastStore()
  toast.push(options.startedMessage, 'info', 5200)

  void (async () => {
    try {
      const result = await options.fetchBlob()
      const blob = result instanceof Blob ? result : result.blob
      const filenameFromFetch = result instanceof Blob ? undefined : result.filename
      const filename = filenameFromFetch
        || (typeof options.filename === 'function' ? options.filename() : options.filename)
      triggerBlobDownload(blob, filename)
      toast.push(resolveMessage(options.successMessage), 'success')
      await options.onSuccess?.()
    } catch (error) {
      const message = options.mapError?.(error)
        ?? apiErrorMessage(error, options.failedMessage)
      toast.push(message, 'error')
    } finally {
      inFlightKeys.delete(key)
      if (options.busy) options.busy.value = false
      options.onBusyChange?.(false)
    }
  })()
}
